import * as React from "react";
import { useParams } from "@tanstack/react-router";
import { cn } from "../shared/ui/index.ts";
import { appNavigate, appOpenMessageConversation } from "../core/app-bridge.ts";
import { conversationForPatient } from "../messages/conversations.ts";
import { Interactions } from "../core/providers/interactions.provider.tsx";
import {
  paths,
  usePracticeUuid,
  type PatientModuleId,
} from "../core/services/navigation.service.ts";
import {
  patientsService,
  type Patient,
} from "../core/services/patients.service.ts";
import { I } from "../shared/icons/icons.tsx";
import { PatientAvatar } from "../shared/patient/patient-avatar.component.tsx";
import { PatientNameDisplay } from "../shared/patient/patient-name-display.component.tsx";
import { patientNameParts } from "../shared/patient/patient-name.ts";
import {
  headerBtnAccent,
  headerBtnSecondary,
  miniIconBtn,
  pm,
} from "../shared/page-layout.ts";
import { MediaModule } from "./media-module.component.tsx";
import { OptionStepper } from "../shared/selection-nav.component.tsx";
import { ObjectivesContent } from "../dashboard/objectives.widget.tsx";
import { ClinicalCommentsWidget } from "../dashboard/clinical-comments.widget.tsx";
import { DiagnosticWidget } from "../dashboard/diagnostic.widget.tsx";
import { TreatmentPlanWidget } from "../dashboard/treatment-plan.widget.tsx";

type IconName = keyof typeof I;
function pmIcon(name: string) {
  return I[name as IconName] ?? I.Circle;
}

function patientSeed(p: Patient): number {
  const idx = patientsService.list().findIndex((x) => x.uuid === p.uuid);
  return idx >= 0 ? idx + 1 : 1;
}

// ---------- Shared module shell (patient hero + module body) ----------
export function PatientModulePage() {
  const practiceUuid = usePracticeUuid();
  const { patientUuid, module } = useParams({ strict: false });
  const moduleId = module as PatientModuleId;
  const { current, selectPatient } = Interactions.usePatient();
  const toast = Interactions.useToast();
  const [civilEdit, setCivilEdit] = React.useState(false);

  const patientFromUrl = patientUuid
    ? patientsService.findByUuid(patientUuid)
    : undefined;

  React.useEffect(() => {
    if (patientFromUrl && patientFromUrl.uuid !== current?.uuid) {
      selectPatient(patientFromUrl);
    }
  }, [patientFromUrl, current?.uuid, selectPatient]);

  React.useEffect(() => {
    if (moduleId !== "civil") setCivilEdit(false);
  }, [moduleId]);

  const p = patientFromUrl ?? current;

  if (!p) {
    return (
      <div className="flex flex-1 items-center justify-center p-10 text-sf-text-muted">
        <div className="text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-sf-surface-2">
            <I.User size={28} />
          </div>
          <div className="mb-1 text-base font-semibold text-sf-text">
            Aucun patient sélectionné
          </div>
          <div className="mb-4 text-[13px]">
            Sélectionnez un patient depuis la liste, l'agenda ou la recherche.
          </div>
          <button
            type="button"
            onClick={() => appNavigate(paths.patients(practiceUuid))}
            className={cn(pm.btnPrimary, "h-8")}
          >
            Voir la liste des patients
          </button>
        </div>
      </div>
    );
  }

  const seed = patientSeed(p);
  const dob = `${15 + (seed % 14)}/${String(((seed * 3) % 12) + 1).padStart(2, "0")}/${1990 - p.age + 34}`;

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-sf-surface-alt">
      {/* Patient hero — compact sur mobile, riche sur desktop */}
      <div
        className={cn(
          "flex shrink-0 items-center gap-2 border-b border-sf-border bg-sf-surface",
          "px-2 py-2 sm:px-4 sm:py-2.5",
        )}
      >
        {/* Avatar unique, taille moyenne, masqué sur lg+ (la sidebar
         *  PatientCard montre déjà le patient sélectionné). */}
        <PatientAvatar
          patient={p}
          size={40}
          ring
          online
          className="shrink-0 lg:hidden"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <h1 className="m-0">
              <PatientNameDisplay
                name={p.name}
                lastClassName="text-[13px] font-semibold uppercase tracking-wide text-sf-text-secondary sm:text-base"
                firstClassName="text-[14.5px] font-bold tracking-tight text-sf-text sm:text-lg"
              />
            </h1>
            <span className="hidden rounded border border-sf-primary-100 bg-sf-primary-50 px-1.5 py-0.5 text-[10.5px] font-semibold text-sf-primary sm:inline-block">
              {p.tag}
            </span>
            <span className="hidden items-center gap-1 text-[11px] text-sf-text-muted sm:inline-flex">
              <I.Circle size={6} className="fill-green-600 stroke-green-600" />{" "}
              Dossier actif
            </span>
          </div>
          {/* Sous-ligne : courte sur mobile (juste âge + sexe),
           *  complète sur desktop. */}
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0 text-[11px] text-sf-text-secondary sm:gap-4 sm:text-xs">
            <span>
              <strong className="text-sf-text">{p.age} ans</strong>
              <span className="hidden sm:inline">
                {" "}
                · {p.sex === "F" ? "Femme" : "Homme"}
              </span>
              <span className="sm:hidden"> · {p.sex === "F" ? "F" : "H"}</span>
            </span>
            <span className="hidden sm:inline">Né(e) le {dob}</span>
            <span className="hidden items-center gap-1 sm:inline-flex">
              <I.Phone size={11} />
              {p.phone}
            </span>
            <span className="hidden sm:inline">Dernier RDV : {p.last}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
          {moduleId === "civil" &&
            (civilEdit ? (
              <>
                <button
                  type="button"
                  onClick={() => setCivilEdit(false)}
                  className={cn(headerBtnSecondary, "hidden sm:inline-flex")}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCivilEdit(false);
                    toast.push({
                      type: "success",
                      title: "Fiche civile mise à jour",
                    });
                  }}
                  className={cn(headerBtnAccent, "px-2 sm:px-3.5")}
                  title="Enregistrer"
                >
                  <I.Check size={13} />
                  <span className="hidden sm:inline">Enregistrer</span>
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setCivilEdit(true)}
                className={cn(headerBtnSecondary, "px-2 sm:px-3.5")}
                title="Modifier"
              >
                <I.Edit size={13} />
                <span className="hidden sm:inline">Modifier</span>
              </button>
            ))}
          <button
            type="button"
            onClick={() => {
              const conv = conversationForPatient(p.name);
              appOpenMessageConversation(conv.id);
              toast.push({
                type: "info",
                title: `Conversation avec ${p.name.split(" ")[0]} ouverte`,
              });
            }}
            className={cn(headerBtnSecondary, "px-2 sm:px-3.5")}
            title="Envoyer un message"
          >
            <I.MessageSquare size={13} />
            <span className="hidden sm:inline">Message</span>
          </button>
          <button
            type="button"
            onClick={() =>
              toast.push({ type: "success", title: "Dossier exporté en PDF" })
            }
            className={miniIconBtn()}
            title="Exporter"
          >
            <I.Download size={13} />
          </button>
        </div>
      </div>

      {/* Module content */}
      <div
        className={cn(
          "min-h-0 flex-1",
          moduleId === "media"
            ? "overflow-hidden"
            : "overflow-y-auto p-2.5 sm:p-4.5",
        )}
      >
        {moduleId === "clinical" && <ClinicalModule patient={p} />}
        {moduleId === "civil" && <CivilModule patient={p} edit={civilEdit} />}
        {moduleId === "billing" && <BillingModule patient={p} />}
        {moduleId === "history" && <HistoryModule />}
        {moduleId === "media" && <MediaModule patient={p} />}
        {moduleId === "exercises" && <ExercisesModule />}
      </div>
    </div>
  );
}

// ---------- Shared layout primitives ----------
function PMCard({
  title,
  subtitle,
  action,
  children,
  padding = 20,
}: {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  padding?: number;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-sf-border bg-sf-surface">
      {(title || action) && (
        <div className="flex items-center justify-between border-b border-sf-border px-[18px] py-3.5">
          <div>
            {title && (
              <div className="text-[13px] font-semibold text-sf-text">
                {title}
              </div>
            )}
            {subtitle && (
              <div className="mt-0.5 text-[11.5px] text-sf-text-muted">
                {subtitle}
              </div>
            )}
          </div>
          {action}
        </div>
      )}
      <div className={padding === 0 ? "p-0" : "p-5"}>{children}</div>
    </div>
  );
}
function PMGrid({
  cols = 2,
  gap = 16,
  children,
}: {
  cols?: number;
  gap?: number;
  children: React.ReactNode;
}) {
  // Mobile : 1 col, sm : 2 cols, lg : cols cible. Évite l'écrasement
  // quand cols=4 sur une largeur étroite.
  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2",
        gap === 14 ? "gap-2.5 sm:gap-3.5" : "gap-3 sm:gap-4",
        cols === 3 && "lg:grid-cols-3",
        cols === 4 && "lg:grid-cols-4",
      )}
    >
      {children}
    </div>
  );
}
function PMTag({
  color = "gray",
  children,
}: {
  color?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "whitespace-nowrap rounded border px-2 py-0.5 text-[11px] font-semibold",
        color === "gray" &&
          "border-sf-border bg-sf-surface-2 text-sf-text-secondary",
        color === "red" && "border-red-200 bg-red-50 text-red-700",
        color === "amber" && "border-amber-200 bg-amber-50 text-amber-800",
        color === "green" && "border-green-200 bg-green-50 text-green-700",
        color === "blue" && "border-blue-200 bg-blue-50 text-blue-800",
        color === "indigo" &&
          "border-sf-primary-100 bg-sf-primary-50 text-sf-primary",
      )}
    >
      {children}
    </span>
  );
}
const CLINICAL_TABS = [
  { id: "comments", label: "Commentaires", Icon: I.FileText },
  { id: "diagnostic", label: "Diagnostic", Icon: I.Stethoscope },
  { id: "treatment", label: "Plan de traitement", Icon: I.ListChecks },
  { id: "objectives", label: "Objectifs", Icon: I.Target },
] as const;

function ClinicalModule(_: { patient: Patient }) {
  void _;
  const [activeTab, setActiveTab] = React.useState<string>("comments");

  return (
    <div className="flex flex-col">
      {/* Onglets — mêmes que le groupe Clinique du dashboard */}
      <div className="mb-4 flex gap-1 rounded-lg border border-sf-border bg-sf-surface-alt p-1">
        {CLINICAL_TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={cn(
              "flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-[12px] font-medium transition-all",
              activeTab === id
                ? "bg-white text-sf-primary shadow-sm"
                : "text-sf-text-secondary hover:bg-white/60 hover:text-sf-text",
            )}
          >
            <Icon
              size={13}
              className={
                activeTab === id ? "text-sf-primary" : "text-sf-text-muted"
              }
            />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Contenu — mêmes composants que les widgets du dashboard */}
      {activeTab === "comments" && <ClinicalCommentsWidget />}
      {activeTab === "diagnostic" && <DiagnosticWidget />}
      {activeTab === "treatment" && <TreatmentPlanWidget />}
      {activeTab === "objectives" && <ObjectivesContent />}
    </div>
  );
}

// ====== 2. ÉTAT CIVIL ======
export function CivilModule({
  patient,
  edit,
}: {
  patient: Patient;
  edit: boolean;
}) {
  const toast = Interactions.useToast();
  const seed = patientSeed(patient);
  const dob = `${15 + (seed % 14)}/${String(((seed * 3) % 12) + 1).padStart(2, "0")}/${1990 - patient.age + 34}`;
  const [data, setData] = React.useState({
    firstName: patientNameParts(patient.name).first,
    lastName: patientNameParts(patient.name).last,
    dob,
    pob: "Lyon, France",
    nationality: "Française",
    nss: "1 85 12 75 116 023",
    address: "14 rue des Lilas",
    zip: "69003",
    city: "Lyon",
    phone: patient.phone,
    mobile: patient.phone,
    email: (() => {
      const { first, last } = patientNameParts(patient.name);
      return `${(first || "patient").toLowerCase()}.${(last || "x").toLowerCase()}@email.fr`;
    })(),
    profession: "Cadre administratif",
    maritalStatus: "Marié(e)",
    children: "2",
    emergencyName: "Jean Lefèvre",
    emergencyRelation: "Conjoint",
    emergencyPhone: "06 11 22 33 44",
    insurer: "CPAM Rhône",
    mutuelle: "Harmonie Mutuelle",
    mutuelleId: `HM-${patientSeed(patient) * 12345}`,
    doctor: "Dr. Camille Moreau",
    consent: true,
  });

  const completedFields = [
    data.firstName,
    data.lastName,
    data.dob,
    data.pob,
    data.nationality,
    data.nss,
    data.address,
    data.zip,
    data.city,
    data.phone,
    data.mobile,
    data.email,
    data.profession,
    data.insurer,
    data.doctor,
  ].filter(Boolean).length;
  const totalFields = 15;
  const completionPct = Math.round((completedFields / totalFields) * 100);

  // Section avec icône colorée
  const CivilSection = ({
    title,
    icon,
    accentColor,
    children,
  }: {
    title: string;
    icon: React.ReactNode;
    accentColor: string;
    children: React.ReactNode;
  }) => (
    <div className="overflow-hidden rounded-lg border border-sf-border bg-sf-surface">
      <div
        className={cn(
          "flex items-center gap-2.5 border-b border-sf-border px-4.5 py-3",
          accentColor,
        )}
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/60">
          {icon}
        </div>
        <span className="text-[13px] font-semibold">{title}</span>
      </div>
      <div className="p-5">
        <div className="grid grid-cols-3 gap-3.5">{children}</div>
      </div>
    </div>
  );

  // Champ avec icône
  const Cell = ({
    label,
    value,
    field,
    span = 1,
    icon,
    type = "text",
    highlight,
  }: {
    label: string;
    value: string;
    field: string;
    span?: number;
    icon?: React.ReactNode;
    type?: string;
    highlight?: boolean;
  }) => (
    <div
      className={cn(
        span === 1 && "col-span-1",
        span === 2 && "col-span-2",
        span === 3 && "col-span-3",
        "group",
      )}
    >
      <div className="mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-sf-text-muted">
        {icon && <span className="opacity-60">{icon}</span>}
        {label}
      </div>
      {edit ? (
        <input
          type={type}
          value={value}
          onChange={(e) => setData((s) => ({ ...s, [field]: e.target.value }))}
          className={cn(pm.input, "transition-shadow focus:shadow-sm")}
        />
      ) : (
        <div
          className={cn(
            "rounded px-2 py-1.5 text-[13px] transition-colors",
            highlight
              ? "border border-amber-200 bg-amber-50 font-semibold text-amber-900"
              : "text-sf-text hover:bg-sf-surface-alt",
            !value && "italic text-sf-text-muted",
          )}
        >
          {value || "—"}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Bandeau de complétion */}
      <div className="flex items-center gap-4 rounded-xl border border-sf-border bg-linear-to-r from-sf-primary-50 via-white to-white px-5 py-4">
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
          <svg
            viewBox="0 0 56 56"
            className="absolute inset-0 h-full w-full -rotate-90"
          >
            <circle
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke="#e2e8f0"
              strokeWidth="4"
            />
            <circle
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke="#4f46e5"
              strokeWidth="4"
              strokeDasharray={`${2 * Math.PI * 24}`}
              strokeDashoffset={`${2 * Math.PI * 24 * (1 - completionPct / 100)}`}
              strokeLinecap="round"
              className="transition-all duration-700"
            />
          </svg>
          <span className="text-[13px] font-bold text-sf-primary">
            {completionPct}%
          </span>
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-sf-text">
            Fiche civile {completionPct === 100 ? "complète" : "incomplète"}
          </div>
          <div className="mt-0.5 text-[11.5px] text-sf-text-muted">
            {completedFields} champs renseignés sur {totalFields}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-[11px] font-medium text-green-700">
              <I.Check size={10} /> Identité
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-[11px] font-medium text-green-700">
              <I.Check size={10} /> Coordonnées
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-[11px] font-medium text-green-700">
              <I.Check size={10} /> Couverture santé
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-sf-border bg-sf-surface-2 px-2.5 py-0.5 text-[11px] font-medium text-sf-text-muted">
              <I.AlertTriangle size={10} /> Photo manquante
            </span>
          </div>
        </div>
        <div className="shrink-0">
          <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-sf-border bg-sf-surface-alt text-sf-text-muted transition-colors hover:border-sf-primary hover:text-sf-primary cursor-pointer">
            <div className="flex flex-col items-center gap-0.5">
              <I.Camera size={18} />
              <span className="text-[9px] font-medium">Photo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Identité */}
      <CivilSection
        title="Identité"
        icon={<I.User size={15} className="text-sf-primary" />}
        accentColor="bg-linear-to-r from-sf-primary-50 to-white text-sf-primary"
      >
        <Cell
          label="Prénom"
          value={data.firstName}
          field="firstName"
          icon={<I.User size={11} />}
        />
        <Cell
          label="Nom"
          value={data.lastName}
          field="lastName"
          icon={<I.User size={11} />}
        />
        <Cell
          label="Date de naissance"
          value={data.dob}
          field="dob"
          type="text"
          icon={<I.Calendar size={11} />}
        />
        <Cell
          label="Lieu de naissance"
          value={data.pob}
          field="pob"
          icon={<I.MapPin size={11} />}
        />
        <Cell
          label="Nationalité"
          value={data.nationality}
          field="nationality"
          icon={<I.Globe size={11} />}
        />
        <Cell
          label="N° sécurité sociale"
          value={data.nss}
          field="nss"
          icon={<I.Shield size={11} />}
          highlight={!edit}
        />
        <Cell
          label="Profession"
          value={data.profession}
          field="profession"
          icon={<I.Briefcase size={11} />}
        />
        <Cell
          label="Situation familiale"
          value={data.maritalStatus}
          field="maritalStatus"
          icon={<I.Heart size={11} />}
        />
        <Cell
          label="Enfants"
          value={data.children}
          field="children"
          icon={<I.Users size={11} />}
        />
      </CivilSection>

      {/* Coordonnées */}
      <CivilSection
        title="Coordonnées"
        icon={<I.MapPin size={15} className="text-blue-600" />}
        accentColor="bg-linear-to-r from-blue-50 to-white text-blue-700"
      >
        <Cell
          label="Adresse"
          value={data.address}
          field="address"
          span={2}
          icon={<I.MapPin size={11} />}
        />
        <Cell
          label="Code postal"
          value={data.zip}
          field="zip"
          icon={<I.MapPin size={11} />}
        />
        <Cell
          label="Ville"
          value={data.city}
          field="city"
          icon={<I.MapPin size={11} />}
        />
        <Cell
          label="Téléphone fixe"
          value={data.phone}
          field="phone"
          icon={<I.Phone size={11} />}
        />
        <Cell
          label="Mobile"
          value={data.mobile}
          field="mobile"
          icon={<I.Phone size={11} />}
        />
        <Cell
          label="Email"
          value={data.email}
          field="email"
          span={3}
          icon={<I.Mail size={11} />}
        />
      </CivilSection>

      {/* Contact d'urgence */}
      <div className="overflow-hidden rounded-lg border border-red-200 bg-sf-surface">
        <div className="flex items-center gap-2.5 border-b border-red-200 bg-linear-to-r from-red-50 to-white px-4.5 py-3">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/60">
            <I.AlertTriangle size={15} className="text-red-600" />
          </div>
          <span className="text-[13px] font-semibold text-red-700">
            Contact d'urgence
          </span>
          <span className="ml-auto rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-600">
            Prioritaire
          </span>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-3.5">
            <Cell
              label="Nom complet"
              value={data.emergencyName}
              field="emergencyName"
              icon={<I.User size={11} />}
            />
            <Cell
              label="Lien de parenté"
              value={data.emergencyRelation}
              field="emergencyRelation"
              icon={<I.Heart size={11} />}
            />
            <Cell
              label="Téléphone"
              value={data.emergencyPhone}
              field="emergencyPhone"
              icon={<I.Phone size={11} />}
            />
          </div>
        </div>
      </div>

      {/* Couverture santé */}
      <CivilSection
        title="Couverture santé"
        icon={<I.Shield size={15} className="text-green-600" />}
        accentColor="bg-linear-to-r from-green-50 to-white text-green-700"
      >
        <div className="col-span-3 mb-1 grid grid-cols-2 gap-3">
          {/* Carte sécu */}
          <div className="rounded-lg border border-blue-200 bg-linear-to-br from-blue-600 to-blue-800 p-3 text-white">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                Assurance Maladie
              </span>
              <I.Shield size={14} className="opacity-60" />
            </div>
            <div className="text-[15px] font-bold tracking-wider">
              {data.nss}
            </div>
            <div className="mt-1.5 text-[11px] opacity-70">{data.insurer}</div>
          </div>
          {/* Carte mutuelle */}
          <div className="rounded-lg border border-emerald-200 bg-linear-to-br from-emerald-500 to-emerald-700 p-3 text-white">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">
                Mutuelle
              </span>
              <I.Heart size={14} className="opacity-60" />
            </div>
            <div className="text-[15px] font-bold">{data.mutuelle}</div>
            <div className="mt-1.5 text-[11px] opacity-70">
              N° {data.mutuelleId}
            </div>
          </div>
        </div>
        {edit && (
          <>
            <Cell
              label="Assurance maladie"
              value={data.insurer}
              field="insurer"
              icon={<I.Shield size={11} />}
            />
            <Cell
              label="Mutuelle"
              value={data.mutuelle}
              field="mutuelle"
              icon={<I.Heart size={11} />}
            />
            <Cell
              label="N° adhérent mutuelle"
              value={data.mutuelleId}
              field="mutuelleId"
              icon={<I.Hash size={11} />}
            />
          </>
        )}
        <Cell
          label="Médecin traitant"
          value={data.doctor}
          field="doctor"
          span={edit ? 3 : 1}
          icon={<I.User size={11} />}
        />
        {!edit && (
          <>
            <div className="col-span-1" />
            <div className="col-span-1" />
          </>
        )}
      </CivilSection>

      {/* Consentements & RGPD */}
      <PMCard
        title="Consentements & RGPD"
        subtitle="Documents signés par le patient"
      >
        <div className="flex flex-col gap-2">
          {[
            {
              id: "rgpd",
              label: "Consentement RGPD",
              desc: "Traitement des données personnelles",
              date: "10/01/2024",
              signed: true,
              icon: <I.Shield size={14} />,
              color: "blue",
            },
            {
              id: "med",
              label: "Partage du dossier médical",
              desc: "Avec les spécialistes partenaires",
              date: "10/01/2024",
              signed: true,
              icon: <I.FileText size={14} />,
              color: "indigo",
            },
            {
              id: "images",
              label: "Droit à l'image (avant/après)",
              desc: "Photos cliniques et vidéos",
              date: "15/03/2024",
              signed: true,
              icon: <I.Camera size={14} />,
              color: "violet",
            },
            {
              id: "recherche",
              label: "Participation études cliniques",
              desc: "Données anonymisées",
              date: "—",
              signed: false,
              icon: <I.Activity size={14} />,
              color: "gray",
            },
          ].map((c, i) => (
            <div
              key={c.id}
              className={cn(
                "flex items-center justify-between rounded-lg border px-3.5 py-3 transition-all",
                c.signed
                  ? "border-green-200 bg-linear-to-r from-green-50 to-white"
                  : "border-sf-border bg-sf-surface-alt hover:border-amber-200 hover:bg-amber-50/40",
              )}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    c.signed
                      ? "bg-green-100 text-green-600"
                      : "bg-sf-surface-2 text-sf-text-muted",
                  )}
                >
                  {c.icon}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-sf-text">
                      {c.label}
                    </span>
                    {c.signed && (
                      <span className="inline-flex items-center gap-0.5 rounded-full border border-green-300 bg-green-100 px-1.5 py-0.5 text-[9.5px] font-bold text-green-700">
                        <I.Check size={8} /> Signé
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-sf-text-muted">
                    <span>{c.desc}</span>
                    {c.signed && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-0.5">
                          <I.Calendar size={10} /> {c.date}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  toast.push({
                    type: c.signed ? "info" : "success",
                    title: c.signed
                      ? "Document ouvert"
                      : "Demande envoyée au patient",
                  })
                }
                className={cn(
                  pm.btnCompact,
                  c.signed
                    ? "border-green-300 bg-green-100 text-green-700 hover:bg-green-200"
                    : cn(pm.btnSecondary),
                )}
              >
                {c.signed ? (
                  <span className="flex items-center gap-1">
                    <I.Eye size={11} /> Consulter
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <I.Send size={11} /> Demander
                  </span>
                )}
              </button>
            </div>
          ))}
        </div>
      </PMCard>
    </div>
  );
}

// ====== 3. PLAN DE GESTION ======
export function BillingModule({ patient }: { patient: Patient }) {
  const toast = Interactions.useToast();
  const seed = patientSeed(patient);
  const balance = -47.5 + seed * 12.3;
  const totalInvoiced = 2840 + seed * 50;
  const paid = totalInvoiced - Math.abs(balance);

  const invoices = [
    {
      id: "F-2026-0142",
      date: "20/04/2026",
      desc: "Consultation + Examen",
      amount: 75.0,
      status: "paid",
    },
    {
      id: "F-2026-0118",
      date: "02/04/2026",
      desc: "Téléconsultation",
      amount: 35.0,
      status: "paid",
    },
    {
      id: "F-2026-0091",
      date: "15/03/2026",
      desc: "Consultation longue",
      amount: 65.0,
      status: "pending",
    },
    {
      id: "F-2026-0064",
      date: "28/02/2026",
      desc: "Bilan annuel + analyses",
      amount: 180.0,
      status: "overdue",
    },
    {
      id: "F-2026-0021",
      date: "10/01/2026",
      desc: "Consultation + vaccination",
      amount: 95.0,
      status: "paid",
    },
  ];

  const quotes = [
    {
      id: "D-2026-0033",
      date: "18/04/2026",
      desc: "Programme de soins 6 mois",
      amount: 480.0,
      status: "sent",
    },
    {
      id: "D-2026-0028",
      date: "02/04/2026",
      desc: "Bilan complet + imagerie",
      amount: 320.0,
      status: "accepted",
    },
  ];

  const statusMap = {
    paid: { color: "green", label: "Payée" },
    pending: { color: "amber", label: "En attente" },
    overdue: { color: "red", label: "En retard" },
    sent: { color: "blue", label: "Envoyé" },
    accepted: { color: "green", label: "Accepté" },
    refused: { color: "gray", label: "Refusé" },
  } as const;
  const statusStyle = (s: string) =>
    statusMap[s as keyof typeof statusMap] ?? {
      color: "gray",
      label: s,
    };

  return (
    <div className="flex flex-col gap-4">
      {/* KPI strip */}
      <PMGrid cols={4}>
        {[
          {
            label: "Solde du compte",
            value: balance.toFixed(2),
            unit: "€",
            valueClass: balance < 0 ? "text-red-600" : "text-green-600",
            sub: balance < 0 ? "À recouvrer" : "En faveur du patient",
            icon: "CreditCard",
          },
          {
            label: "Total facturé (12 mois)",
            value: totalInvoiced.toFixed(0),
            unit: "€",
            valueClass: "text-sf-text",
            sub: `${invoices.length} factures`,
            icon: "FileText",
          },
          {
            label: "Payé",
            value: paid.toFixed(0),
            unit: "€",
            valueClass: "text-green-600",
            sub: `${((paid / totalInvoiced) * 100).toFixed(0)}% recouvrement`,
            icon: "CheckCircle",
          },
          {
            label: "Devis en attente",
            value: "480",
            unit: "€",
            valueClass: "text-sf-text",
            sub: "1 devis envoyé",
            icon: "Send",
          },
        ].map((k, i) => {
          const Icn = pmIcon(k.icon);
          return (
            <div
              key={i}
              className="rounded-lg border border-sf-border bg-sf-surface p-4"
            >
              <div className="mb-2.5 flex items-center gap-1.5 text-sf-text-muted">
                <Icn size={12} />
                <span className="text-[11px] font-semibold uppercase tracking-wide">
                  {k.label}
                </span>
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={cn(
                    "text-[26px] font-bold tracking-tight",
                    k.valueClass,
                  )}
                >
                  {k.value}
                </span>
                <span className="text-[13px] text-sf-text-muted">{k.unit}</span>
              </div>
              <div className="mt-1 text-[11.5px] text-sf-text-muted">
                {k.sub}
              </div>
            </div>
          );
        })}
      </PMGrid>

      {/* Actions */}
      {balance < 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-700">
            <I.AlertTriangle size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-red-900">
              Facture en retard de paiement
            </div>
            <div className="mt-0.5 text-xs text-red-950">
              F-2026-0064 · 180 € · échéance dépassée de 38 jours
            </div>
          </div>
          <button
            type="button"
            onClick={() =>
              toast.push({ type: "info", title: "Relance envoyée par email" })
            }
            className={pm.btnDanger}
          >
            Envoyer une relance
          </button>
        </div>
      )}

      {/* Factures */}
      <PMCard
        title="Factures"
        subtitle={`${invoices.length} factures · ${invoices.filter((i) => i.status === "paid").length} payées`}
        action={
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() =>
                toast.push({
                  type: "success",
                  title: "Export Excel téléchargé",
                })
              }
              className={pm.btnSecondary}
            >
              <I.Download size={12} /> Export
            </button>
            <button
              type="button"
              onClick={() =>
                toast.push({ type: "info", title: "Nouvelle facture créée" })
              }
              className={pm.btnPrimary}
            >
              <I.Plus size={12} /> Facturer
            </button>
          </div>
        }
        padding={0}
      >
        <div>
          {/* En-tête colonnes masqué sur mobile (la mise en page passe en cartes). */}
          <div className="hidden grid-cols-[130px_110px_1fr_100px_110px_80px] gap-3.5 border-b border-t border-sf-border bg-sf-surface-alt px-[18px] py-2.5 text-[11px] font-semibold uppercase tracking-wide text-sf-text-muted lg:grid">
            <span>N°</span>
            <span>Date</span>
            <span>Description</span>
            <span className="text-right">Montant</span>
            <span>Statut</span>
            <span />
          </div>
          {invoices.map((inv, i) => {
            const st = statusStyle(inv.status);
            return (
              <BillingRow
                key={i}
                first={i === 0}
                last={i === invoices.length - 1}
                id={inv.id}
                date={inv.date}
                desc={inv.desc}
                amount={inv.amount}
                statusColor={st.color}
                statusLabel={st.label}
                onView={() =>
                  toast.push({
                    type: "info",
                    title: `Facture ${inv.id} ouverte`,
                  })
                }
              />
            );
          })}
        </div>
      </PMCard>

      {/* Devis */}
      <PMCard
        title="Devis"
        action={
          <button
            type="button"
            onClick={() =>
              toast.push({ type: "info", title: "Nouveau devis créé" })
            }
            className={pm.btnPrimary}
          >
            <I.Plus size={12} /> Devis
          </button>
        }
        padding={0}
      >
        <div>
          {quotes.map((q, i) => {
            const st = statusStyle(q.status);
            return (
              <BillingRow
                key={i}
                first={i === 0}
                last={i === quotes.length - 1}
                id={q.id}
                date={q.date}
                desc={q.desc}
                amount={q.amount}
                statusColor={st.color}
                statusLabel={st.label}
                onView={() =>
                  toast.push({ type: "info", title: "Devis ouvert" })
                }
              />
            );
          })}
        </div>
      </PMCard>
    </div>
  );
}

function BillingRow({
  first,
  last,
  id,
  date,
  desc,
  amount,
  statusColor,
  statusLabel,
  onView,
}: {
  first: boolean;
  last: boolean;
  id: string;
  date: string;
  desc: string;
  amount: number;
  statusColor: string;
  statusLabel: string;
  onView: () => void;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 px-3 py-2.5 sm:gap-1.5 lg:grid lg:grid-cols-[130px_110px_1fr_100px_110px_80px] lg:items-center lg:gap-3.5 lg:px-[18px] lg:py-3",
        !last && "border-b border-sf-border",
        !first && "lg:border-t-0",
      )}
    >
      {/* Mobile : ligne 1 — N° + Date + Status */}
      <div className="flex items-center gap-2 lg:contents">
        <span className="font-mono text-[11.5px] text-sf-text-secondary lg:text-[12.5px]">
          {id}
        </span>
        <span className="text-[11.5px] text-sf-text-secondary lg:text-[12.5px]">
          {date}
        </span>
        <span className="ml-auto lg:ml-0 lg:hidden">
          <PMTag color={statusColor}>{statusLabel}</PMTag>
        </span>
      </div>
      {/* Description */}
      <span className="text-[13px] text-sf-text">{desc}</span>
      {/* Mobile : ligne 3 — Montant + bouton Voir */}
      <div className="flex items-center justify-between gap-2 lg:contents">
        <span className="text-[14.5px] font-bold text-sf-text lg:text-right lg:text-[13px] lg:font-semibold">
          {amount.toFixed(2)} €
        </span>
        <span className="hidden lg:inline">
          <PMTag color={statusColor}>{statusLabel}</PMTag>
        </span>
        <button type="button" onClick={onView} className={pm.btnGhostRow}>
          Voir
        </button>
      </div>
    </div>
  );
}

// ====== 4. HISTORIQUE ======
export function HistoryModule() {
  const toast = Interactions.useToast();
  const [filter, setFilter] = React.useState("all");

  const events = [
    {
      date: "20/04/2026",
      time: "14:30",
      type: "consultation",
      icon: "Stethoscope",
      color: "#6366f1",
      title: "Consultation de suivi",
      desc: "TA 128/82 · Pas de symptôme nouveau. Renouvellement du traitement.",
      tags: ["Suivi"],
    },
    {
      date: "20/04/2026",
      time: "14:55",
      type: "prescription",
      icon: "FileText",
      color: "#f59e0b",
      title: "Ordonnance",
      desc: "Lévothyrox 50 µg · 3 mois",
      tags: ["Prescription"],
    },
    {
      date: "15/04/2026",
      time: "11:00",
      type: "lab",
      icon: "Droplet",
      color: "#06b6d4",
      title: "Résultats biologiques",
      desc: "Glycémie, TSH, cholestérol — tous dans la norme.",
      tags: ["Labo"],
    },
    {
      date: "02/04/2026",
      time: "16:15",
      type: "consultation",
      icon: "Video",
      color: "#10b981",
      title: "Téléconsultation",
      desc: "Renouvellement contraception. Aucun effet indésirable.",
      tags: ["Téléconsult"],
    },
    {
      date: "15/03/2026",
      time: "09:30",
      type: "consultation",
      icon: "Stethoscope",
      color: "#6366f1",
      title: "Consultation longue",
      desc: "Symptômes anxieux post-événement familial. Orientation psy.",
      tags: ["Consultation"],
    },
    {
      date: "28/02/2026",
      time: "08:00",
      type: "lab",
      icon: "Activity",
      color: "#06b6d4",
      title: "Bilan annuel",
      desc: "Examen clinique complet + ECG + bilan sanguin.",
      tags: ["Bilan"],
    },
    {
      date: "28/02/2026",
      time: "09:15",
      type: "imaging",
      icon: "Image",
      color: "#a855f7",
      title: "Radiographie thoracique",
      desc: "Pas d'anomalie pulmonaire ni cardiaque.",
      tags: ["Imagerie"],
    },
    {
      date: "10/01/2026",
      time: "15:00",
      type: "consultation",
      icon: "Stethoscope",
      color: "#6366f1",
      title: "Consultation + vaccin grippe",
      desc: "Vaccin antigrippal administré. RAS.",
      tags: ["Vaccin"],
    },
    {
      date: "12/12/2025",
      time: "10:30",
      type: "message",
      icon: "MessageSquare",
      color: "#64748b",
      title: "Message reçu",
      desc: "Question sur dosage Lévothyrox — réponse envoyée.",
      tags: ["Message"],
    },
    {
      date: "20/11/2025",
      time: "14:00",
      type: "consultation",
      icon: "ClipboardList",
      color: "#6366f1",
      title: "Premier RDV",
      desc: "Anamnèse complète. Création du dossier.",
      tags: ["Premier RDV"],
    },
  ];

  const filtered =
    filter === "all" ? events : events.filter((e) => e.type === filter);
  const groups = filtered.reduce<Record<string, typeof events>>((acc, e) => {
    const month = e.date.split("/").slice(1).join("/");
    (acc[month] ||= []).push(e);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <OptionStepper
          ariaLabel="Filtrer l'historique"
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "Tout", badge: events.length },
            {
              value: "consultation",
              label: "Consultations",
              badge: events.filter((e) => e.type === "consultation").length,
            },
            {
              value: "prescription",
              label: "Prescriptions",
              badge: events.filter((e) => e.type === "prescription").length,
            },
            {
              value: "lab",
              label: "Examens",
              badge: events.filter((e) => e.type === "lab").length,
            },
            {
              value: "imaging",
              label: "Imagerie",
              badge: events.filter((e) => e.type === "imaging").length,
            },
            {
              value: "message",
              label: "Messages",
              badge: events.filter((e) => e.type === "message").length,
            },
          ]}
        />
        <div className="min-w-2 flex-1" />
        <button
          type="button"
          onClick={() =>
            toast.push({ type: "success", title: "Historique exporté" })
          }
          className={pm.btnSecondary}
        >
          <I.Download size={12} /> Exporter
        </button>
      </div>

      {/* Timeline */}
      <PMCard padding={0}>
        <div className="p-6">
          {Object.entries(groups).map(([month, items]) => (
            <div key={month} className="mb-6 last:mb-0">
              <div className="mb-3 text-[11px] font-bold uppercase tracking-wider text-sf-text-muted">
                {monthLabel(month)}
              </div>
              <div className="relative pl-7">
                <div className="absolute bottom-2 left-[11px] top-2 w-px bg-sf-border" />
                {items.map((e, i) => {
                  const Icn = pmIcon(e.icon);
                  return (
                    <div
                      key={i}
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        toast.push({
                          type: "info",
                          title: e.title,
                          message: e.desc,
                        })
                      }
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          toast.push({
                            type: "info",
                            title: e.title,
                            message: e.desc,
                          });
                        }
                      }}
                      className="relative mb-3.5 cursor-pointer last:mb-0"
                    >
                      <div
                        className={cn(
                          pm.historyDot,
                          "absolute -left-7 top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white",
                        )}
                        style={
                          {
                            ["--pm-history-accent"]: e.color,
                          } as React.CSSProperties
                        }
                      >
                        <Icn size={11} />
                      </div>
                      <div className={pm.historyRow}>
                        <div className="flex items-baseline justify-between gap-2.5">
                          <div className="text-[13px] font-semibold">
                            {e.title}
                          </div>
                          <div className="whitespace-nowrap text-[11.5px] text-sf-text-muted">
                            {e.date} · {e.time}
                          </div>
                        </div>
                        <div className="mt-1 text-[12.5px] leading-relaxed text-sf-text-secondary">
                          {e.desc}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </PMCard>
    </div>
  );
}
function monthLabel(mmYYYY: string) {
  const [m, y] = mmYYYY.split("/");
  const names = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];
  return `${names[parseInt(m ?? "1", 10) - 1] ?? mmYYYY} ${y ?? ""}`;
}

// ====== 6. SUIVI EXERCICES ======
function ExercisesModule() {
  const toast = Interactions.useToast();
  const [program, setProgram] = React.useState([
    {
      id: 1,
      name: "Étirements lombaires",
      duration: "5 min",
      reps: "3 × 30s",
      freq: "Quotidien",
      done: [1, 1, 1, 0, 1, 1, 0],
      adherence: 71,
    },
    {
      id: 2,
      name: "Renforcement gainage",
      duration: "8 min",
      reps: "3 × 45s",
      freq: "5j/semaine",
      done: [1, 1, 1, 1, 1, 0, 0],
      adherence: 86,
    },
    {
      id: 3,
      name: "Mobilisations cervicales",
      duration: "3 min",
      reps: "2 × 10",
      freq: "Quotidien",
      done: [1, 1, 0, 1, 1, 1, 1],
      adherence: 86,
    },
    {
      id: 4,
      name: "Marche active",
      duration: "30 min",
      reps: "—",
      freq: "4j/semaine",
      done: [1, 0, 1, 0, 1, 1, 0],
      adherence: 57,
    },
    {
      id: 5,
      name: "Respiration cohérence",
      duration: "5 min",
      reps: "6/min",
      freq: "2× par jour",
      done: [1, 1, 1, 0, 1, 0, 1],
      adherence: 71,
    },
  ]);

  const overallAdherence = Math.round(
    program.reduce((s, p) => s + p.adherence, 0) / program.length,
  );
  const totalThisWeek = program.reduce(
    (s, p) => s + p.done.filter((d) => d).length,
    0,
  );
  const expectedThisWeek = program.length * 7;

  const days = ["L", "M", "M", "J", "V", "S", "D"];

  return (
    <div className="flex flex-col gap-4">
      {/* KPIs */}
      <PMGrid cols={4}>
        <KPI
          label="Adhérence globale"
          value={overallAdherence + "%"}
          sub="30 derniers jours"
          icon="Activity"
          valueClass={
            overallAdherence >= 75 ? "text-green-600" : "text-amber-500"
          }
        />
        <KPI
          label="Séances cette semaine"
          value={`${totalThisWeek}/${expectedThisWeek}`}
          sub="Séances réalisées"
          icon="CheckCircle"
          valueClass="text-indigo-500"
        />
        <KPI
          label="Série en cours"
          value="14 j"
          sub="Record : 28 j"
          icon="Zap"
          valueClass="text-amber-500"
        />
        <KPI
          label="Exercices actifs"
          value={program.length}
          sub="Programme personnalisé"
          icon="ListChecks"
          valueClass="text-sf-text"
        />
      </PMGrid>

      {/* Programme hebdomadaire */}
      <PMCard
        title="Programme hebdomadaire"
        subtitle="Semaine du 20 — 26 avril 2026"
        action={
          <button
            type="button"
            onClick={() =>
              toast.push({
                type: "info",
                title: "Nouvel exercice ajouté au programme",
              })
            }
            className={pm.btnPrimary}
          >
            <I.Plus size={12} /> Ajouter
          </button>
        }
        padding={0}
      >
        <div>
          <div className="grid grid-cols-[1.6fr_110px_110px_130px_repeat(7,32px)_80px] items-center gap-2.5 border-b border-t border-sf-border bg-sf-surface-alt px-[18px] py-2.5 text-[11px] font-semibold uppercase tracking-wide text-sf-text-muted">
            <span>Exercice</span>
            <span>Durée</span>
            <span>Répétitions</span>
            <span>Fréquence</span>
            {days.map((d, i) => (
              <span key={i} className="text-center">
                {d}
              </span>
            ))}
            <span className="text-right">Adhérence</span>
          </div>
          {program.map((p, idx) => (
            <div
              key={p.id}
              className={cn(
                "grid grid-cols-[1.6fr_110px_110px_130px_repeat(7,32px)_80px] items-center gap-2.5 px-[18px] py-3.5",
                idx < program.length - 1 && "border-b border-sf-border",
              )}
            >
              <div className="text-[13px] font-semibold">{p.name}</div>
              <div className="text-[12.5px] text-sf-text-secondary">
                {p.duration}
              </div>
              <div className="text-[12.5px] text-sf-text-secondary">
                {p.reps}
              </div>
              <div className="text-xs text-sf-text-muted">{p.freq}</div>
              {p.done.map((d, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => {
                    setProgram((s) =>
                      s.map((x) =>
                        x.id === p.id
                          ? {
                              ...x,
                              done: x.done.map((v, j) =>
                                j === i ? (v ? 0 : 1) : v,
                              ),
                            }
                          : x,
                      ),
                    );
                  }}
                  className={cn(
                    "mx-auto flex h-[22px] w-[22px] items-center justify-center rounded-[5px] border",
                    d
                      ? "border-green-600 bg-green-600 text-white"
                      : "border-sf-border-strong bg-transparent",
                  )}
                >
                  {d ? <I.Check size={12} /> : null}
                </button>
              ))}
              <div className="text-right">
                <PMTag
                  color={
                    p.adherence >= 75
                      ? "green"
                      : p.adherence >= 50
                        ? "amber"
                        : "red"
                  }
                >
                  {p.adherence}%
                </PMTag>
              </div>
            </div>
          ))}
        </div>
      </PMCard>

      {/* Évolution + Notes */}
      <PMGrid cols={2}>
        <PMCard
          title="Évolution de l'adhérence"
          subtitle="12 dernières semaines"
        >
          <AdherenceChart />
        </PMCard>

        <PMCard title="Notes & ressenti patient">
          <div className="flex flex-col gap-2.5">
            {[
              {
                date: "25/04",
                mood: "😊",
                text: "Lombalgie moins présente après 2 semaines. Exercices bien tolérés.",
              },
              {
                date: "20/04",
                mood: "😐",
                text: "Difficulté à faire la marche active à cause de la pluie. Compensé par + de gainage.",
              },
              {
                date: "15/04",
                mood: "😊",
                text: "Très bonne semaine, j'ai tenu tous les jours sauf samedi.",
              },
              {
                date: "08/04",
                mood: "😣",
                text: "Douleur cervicale en début de semaine, ai arrêté 2 jours puis repris.",
              },
            ].map((n, i) => (
              <div
                key={i}
                className="flex gap-2.5 rounded-md border border-sf-border bg-sf-surface-alt px-3 py-2.5"
              >
                <div className="shrink-0 text-lg">{n.mood}</div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 text-[11.5px] text-sf-text-muted">
                    {n.date}
                  </div>
                  <div className="text-[12.5px] leading-relaxed text-sf-text-secondary">
                    {n.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PMCard>
      </PMGrid>
    </div>
  );
}

function KPI({
  label,
  value,
  sub,
  icon,
  valueClass,
}: {
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: string;
  valueClass?: string;
}) {
  const Icn = pmIcon(icon);
  return (
    <div className="rounded-lg border border-sf-border bg-sf-surface p-4">
      <div className="mb-2.5 flex items-center gap-1.5 text-sf-text-muted">
        <Icn size={12} />
        <span className="text-[11px] font-semibold uppercase tracking-wide">
          {label}
        </span>
      </div>
      <div className={cn("text-[26px] font-bold tracking-tight", valueClass)}>
        {value}
      </div>
      <div className="mt-1 text-[11.5px] text-sf-text-muted">{sub}</div>
    </div>
  );
}

function AdherenceChart() {
  const weeks = [62, 58, 71, 65, 73, 68, 75, 82, 78, 84, 79, 86];
  const max = 100;
  return (
    <div>
      <svg viewBox="0 0 320 160" width="100%" height="180" className="block">
        {/* Y-axis labels */}
        {[0, 25, 50, 75, 100].map((v) => {
          const y = 140 - (v / max) * 120;
          return (
            <g key={v}>
              <line
                x1="30"
                y1={y}
                x2="320"
                y2={y}
                stroke="var(--sf-border)"
                strokeWidth=".5"
              />
              <text x="0" y={y + 3} fontSize="9" fill="var(--sf-text-muted)">
                {v}%
              </text>
            </g>
          );
        })}
        {/* Target line at 75% */}
        <line
          x1="30"
          y1={140 - (75 / max) * 120}
          x2="320"
          y2={140 - (75 / max) * 120}
          stroke="#16a34a"
          strokeWidth="1"
          strokeDasharray="3 3"
          opacity=".6"
        />
        <text
          x="298"
          y={140 - (75 / max) * 120 - 3}
          fontSize="9"
          fill="#16a34a"
          fontWeight="600"
        >
          Cible 75%
        </text>
        {/* Path */}
        <path
          d={
            weeks
              .map(
                (v, i) =>
                  `${i === 0 ? "M" : "L"} ${30 + i * 25} ${140 - (v / max) * 120}`,
              )
              .join(" ") + ` L ${30 + (weeks.length - 1) * 25} 140 L 30 140 Z`
          }
          fill="url(#area-grad)"
          opacity=".15"
        />
        <defs>
          <linearGradient id="area-grad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="var(--sf-primary)" />
            <stop offset="100%" stopColor="var(--sf-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d={weeks
            .map(
              (v, i) =>
                `${i === 0 ? "M" : "L"} ${30 + i * 25} ${140 - (v / max) * 120}`,
            )
            .join(" ")}
          fill="none"
          stroke="var(--sf-primary)"
          strokeWidth="2"
        />
        {weeks.map((v, i) => (
          <circle
            key={i}
            cx={30 + i * 25}
            cy={140 - (v / max) * 120}
            r="3"
            fill="white"
            stroke="var(--sf-primary)"
            strokeWidth="2"
          />
        ))}
        {/* X labels */}
        {weeks.map(
          (_, i) =>
            i % 2 === 0 && (
              <text
                key={i}
                x={30 + i * 25}
                y={156}
                fontSize="9"
                fill="var(--sf-text-muted)"
                textAnchor="middle"
              >
                S-{weeks.length - i}
              </text>
            ),
        )}
      </svg>
    </div>
  );
}
